//#region IMPORTS
import React, { Fragment } from 'react';
import { Row, Col, FormGroup, ButtonGroup, Form } from 'react-bootstrap';
import LabelTitle from '../../../components/Label/labelTitle';
import LoadingTableData from '../../../components/Loading/LoadingTableData';
import Button from '../../../components/CustomButton/CustomButton';
import language from '../../../config/es';
import { makeStyles } from '@material-ui/core/styles';
import DataTable from 'react-data-table-component';
//material
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { ExpandableComponent } from '../conciliationShared';
import { FILTER_FIELDS, paginationOptions } from './filterConfig';
import useConciliationPartial from './useConciliationPartial';
//#endregion IMPORTS

//#region STYLES
const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3),
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  marginFilterTop: {
    marginTop: theme.spacing(3),
  },
  marginCol: {
    marginLeft: '10px',
    marginRight: '10px'
  }
}));
//#endregion STYLES

//#region COMPONENTS
// Campo de texto de filtro con su mensaje de error
const FilterField = ({ field, dataFilter, onChange }) => (
  <FormGroup>
    <LabelTitle text={field.label} />
    <Form.Control
      disabled={dataFilter[field.flag] === false}
      maxLength={field.maxLength}
      size="sm"
      type="text"
      name={field.name}
      id={field.name + 'Id'}
      placeholder={field.placeholder}
      value={dataFilter[field.name].value}
      onChange={onChange}
    />
    <span className="small-font text-danger">
      {dataFilter[field.name].error}
    </span>
  </FormGroup>
);

// Interruptores para activar/desactivar cada filtro
const FilterSwitches = ({ dataFilter, onChangeSelect }) => (
  <>
    {FILTER_FIELDS.map((field) => (
      <FormControlLabel
        key={field.flag}
        control={<Switch size="small" checked={dataFilter[field.flag]} name={field.flag} onChange={(value) => {
          onChangeSelect(value, field.flag);
        }}/>}
        label={field.label}
        labelPlacement="end"
      />
    ))}
  </>
);

// Boton de busqueda
const SearchButton = ({ disabled, onClick }) => (
  <ButtonGroup>
    <Button disabled={disabled} success fill type="button"
      onClick={onClick}>
      <i className="fa fa-search"></i>{' '}
      {language.messages.btnSearch}
    </Button>
  </ButtonGroup>
);

// Distribucion en dos bloques: campos a la izquierda e interruptores a la derecha
const NestedFilterLayout = ({ fields, search, switches, marginClass, codeSapCol }) => (
  <Row>
    <Col xs={12} sm={10} md={10} lg={10}>
      <Row>
        <Col xs={12} sm={6} md={6} lg={6}>{fields[0]}</Col>
        <Col xs={12} sm={6} md={6} lg={6}>{fields[1]}</Col>
      </Row>
      <Row>
        <Col {...codeSapCol}>{fields[2]}</Col>
        <Col xs={1} sm={1} md={1} lg={1} className={marginClass}>{search}</Col>
      </Row>
    </Col>
    <Col xs={12} sm={2} md={2} lg={2} className={marginClass}>{switches}</Col>
  </Row>
);

// Distribucion en una sola fila
const InlineFilterLayout = ({ fields, search, switches, marginClass }) => (
  <Row className={marginClass}>
    <Col xs={6} sm={6} md={6} lg={3}>{fields[0]}</Col>
    <Col xs={6} sm={6} md={6} lg={3}>{fields[1]}</Col>
    <Col xs={12} sm={6} md={6} lg={3}>{fields[2]}</Col>
    <Col xs={3} sm={2} md={1} lg={1} className={marginClass}>{search}</Col>
    <Col xs={6} sm={4} md={4} lg={2} className={marginClass}>{switches}</Col>
  </Row>
);
//#endregion COMPONENTS

/**
 * Monitor parcial de conciliacion generico.
 * @param {object} data datos del monitor
 * @param {function} fetchAction accion redux que obtiene el listado
 * @param {Array} columns columnas de la tabla
 * @param {Array} conditionalRowStyles estilos condicionales de filas (opcional)
 * @param {boolean} highlightOnHover resaltar fila al pasar el cursor
 * @param {boolean} inlineFilters usar la distribucion de filtros en una fila
 * @param {object} codeSapCol tamanos de columna del campo codigo SAP (distribucion anidada)
 * @param {object} resetFlags banderas para limpiar campos al desactivar filtros
 */
const ConciliationPartial = ({
  data = [],
  fetchAction,
  columns,
  conditionalRowStyles,
  highlightOnHover = false,
  inlineFilters = false,
  codeSapCol,
  resetFlags,
}) => {
  const classes = useStyles();
  const {
    dataFilter,
    lsConciliationDaily,
    pendingData,
    totalRows,
    currentPage,
    onChangeField,
    onChangeSelect,
    searConciliationDaily,
    handlePageChange,
    handlePerRowsChange,
    customSort,
  } = useConciliationPartial(data, fetchAction, resetFlags);

  const FilterLayout = inlineFilters ? InlineFilterLayout : NestedFilterLayout;

  return (
    <Fragment>
      <form autoComplete="off">
        <FilterLayout
          fields={FILTER_FIELDS.map((field) => (
            <FilterField key={field.name} field={field} dataFilter={dataFilter} onChange={onChangeField} />
          ))}
          search={<SearchButton disabled={data.pendingData} onClick={searConciliationDaily} />}
          switches={<FilterSwitches dataFilter={dataFilter} onChangeSelect={onChangeSelect} />}
          marginClass={classes.marginFilterTop}
          codeSapCol={codeSapCol}
        />
      </form>
      <DataTable
        noHeader={true}
        data={lsConciliationDaily}
        columns={columns}
        conditionalRowStyles={conditionalRowStyles}
        fixedHeader
        fixedHeaderScrollHeight="600px"
        dense
        pointerOnHover={true}
        highlightOnHover={highlightOnHover}
        pagination
        paginationTotalRows={totalRows}
        paginationDefaultPage={currentPage}
        paginationServer
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        onSort={customSort}
        sortServer
        paginationComponentOptions={paginationOptions}
        progressPending={pendingData}
        progressComponent={<LoadingTableData />}
        persistTableHead
        expandableRows
        expandableRowsComponent={ExpandableComponent}
      />
    </Fragment>
  );
};

export default ConciliationPartial;
